// src/components/CanvasContainer.js
import React, { Suspense, lazy, useMemo, useState, useEffect } from "react";
import TopBar from "./common/TopBar.js";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import "../App.css";

const DefaultLayout = lazy(() => import("./layouts/Cards/product_id_1780.js"));
const layoutMap = {

  // ==================================================== //
  // Photo Products Dummy
  4285 : () => import("./layouts/Apparel and Wearable/T-Shirt/product_id_1912.js"),

  // Apparel and Wearable's Layouts Starts
  // Apparel and Wearable -> T-Shirt Category Layouts Starts
  76: () => import("./layouts/Apparel and Wearable/T-Shirt/product_id_1912.js"),
  77: () => import("./layouts/Apparel and Wearable/T-Shirt/product_id_1919.js"),
  78: () => import("./layouts/Apparel and Wearable/T-Shirt/product_id_1926.js"),
  79: () => import("./layouts/Apparel and Wearable/T-Shirt/product_id_1927.js"),
  // Apparel and Wearable -> T-Shirt Category Layouts Ends
  // Apparel and Wearable's Layouts Starts

  // ==================================================== //

  // Calendar's Category Layouts Starts
  // Calendar -> Poster Calendar Category Layouts Starts
  82: () => import("./layouts/Calendar/Poster Calendar/product_id_1937.js"),
  83: () => import("./layouts/Calendar/Poster Calendar/product_id_1939.js"),
  // Calendar -> Poster Calendar Category Layouts Ends

  // Calendar -> Wall Calendar Category Layouts Starts
  1784: () => import("./layouts/Calendar/Wall Calendar/product_id_1986.js"),
  1785: () => import("./layouts/Calendar/Wall Calendar/product_id_1988.js"),
  // Calendar -> Wall Calendar Category Layouts Ends
  // Calendar's Category Layouts Ends

  // ==================================================== //

  // Personalized Items and Gift Idea Cateory Layouts Starts

   // Personalized Items and Gift Idea -> Mugs, Water Bottles, Steins and Drinkables Category Layouts Starts
  147: () => import("./layouts/Personalized Items and Gift Idea/Mugs, Water Bottles, Steins and Drinkables/product_id_2083.js"),
  148: () => import("./layouts/Personalized Items and Gift Idea/Mugs, Water Bottles, Steins and Drinkables/product_id_2085.js"),
  253: () => import("./layouts/Personalized Items and Gift Idea/Mugs, Water Bottles, Steins and Drinkables/product_id_6141.js"),
  // Personalized Items and Gift Idea -> Mugs, Water Bottles, Steins and Drinkables Category Layouts Ends

  // Personalized Items and Gift Idea -> Home and Office and Games Category Layouts Starts
  1786: () => import("./layouts/Personalized Items and Gift Idea/Home and Office/product_id_2050.js"),
  // Personalized Items and Gift Idea -> Home and Office Category Layouts Ends

  // Personalized Items and Gift Idea -> Fun and Games Category Layouts Starts
  1787: () => import("./layouts/Personalized Items and Gift Idea/Fun and Games/product_id_2043.js"),
  // Personalized Items and Gift Idea -> Fun and Games Category Layouts Ends

  // Personalized Items and Gift Idea -> Ornaments Category Layouts Starts
  1788: () => import("./layouts/Personalized Items and Gift Idea/Ornaments/product_id_2031.js"),
  // Personalized Items and Gift Idea -> Ornaments Category Layouts Ends
  // Personalized Items and Gift Idea Cateory Layouts Ends

  // ==================================================== //



  // Cards Category Layouts Starts
  32: () => import("./layouts/Cards/product_id_1773.js"),
  33: () => import("./layouts/Cards/product_id_1775.js"),
  34: () => import("./layouts/Cards/product_id_1778.js"),
  35: () => import("./layouts/Cards/product_id_1780.js"),
  // Cards Category Layouts Ends

  // ==================================================== //

  // Photo Books Cateory Layouts Starts
  75: () => import("./layouts/Photo Books/product_id_1903.js"),
  // Photo Books Cateory Layouts Ends
  
  // ==================================================== //

  // Posters and Collages Cateory Layouts Starts
  36: () => import("./layouts/Posters and Collages/product_id_1822.js"),
  37: () => import("./layouts/Posters and Collages/product_id_1827.js"),
  // Posters and Collages Cateory Layouts Ends

};

// ================= Helpers ================= //
function getQueryParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      productId: params.get("product_id")?.trim() || null,
      layoutId: params.get("layout_id") || null,
      productName: params.get("product_name") || "",
    };
  } catch {
    return { productId: null, layoutId: null, productName: "" };
  }
}

// Detect size/orientation from product name
function detectDetailsFromName(name) {
  if (!name) return { size: null, orientation: null };

  const cleanName = name.replace(/[–—-]/g, "-"); // normalize dash types
  const parts = cleanName.split("-").map((p) => p.trim());
  const lastPart = parts[parts.length - 1] || "";

  // Detect size like "10.5x8.5" or "8x10"
  const sizeMatch = lastPart.match(/(\d+(\.\d+)?)\s*[x×]\s*(\d+(\.\d+)?)/i);
  const size = sizeMatch ? `${sizeMatch[1]}x${sizeMatch[3]}` : null;

  // Detect orientation
  let orientation = null;
  if (/horizontal/i.test(lastPart)) orientation = "Horizontal";
  else if (/vertical/i.test(lastPart)) orientation = "Vertical";
  else if (/square/i.test(lastPart)) orientation = "Square";

  return { size, orientation };
}

// ================= Main Component ================= //
const CanvasContainer = () => {
  const { productId, productName } = useMemo(() => getQueryParams(), []);
  const { size, orientation } = useMemo(
    () => detectDetailsFromName(productName),
    [productName]
  );

  const [layoutExists, setLayoutExists] = useState(true);
  const [LayoutComponent, setLayoutComponent] = useState(() => DefaultLayout);

  // Load dynamic layout
  useEffect(() => {
    if (!productId) {
      setLayoutComponent(() => DefaultLayout);
      return;
    }

    const loader = layoutMap[productId];
    if (loader) {
      loader()
        .then((module) => {
          setLayoutExists(true);
          setLayoutComponent(() => module.default);
        })
        .catch(() => setLayoutExists(false));
    } else {
      setLayoutExists(false);
    }
  }, [productId]);

  // Log detected info (for debugging / dynamic logic)
  useEffect(() => {
    console.log("🧩 Detected from product name:", {
      productName,
      size,
      orientation,
    });
  }, [productName, size, orientation]);

// Use extracted inches directly (no DPI conversion)
function getCanvasDimensions(size, orientation) {
  let width = 10;
  let height = 8;

  if (size && size.includes("x")) {
    const [w, h] = size.split(/[x×]/).map((v) => parseFloat(v.trim()));
    if (!isNaN(w) && !isNaN(h)) {
      width = w;
      height = h;
    }
  }

  // Orientation logic
  if (orientation?.toLowerCase() === "horizontal") {
    [width, height] = [height, width];
  } else if (orientation?.toLowerCase() === "square") {
    const avg = (width + height) / 2;
    width = avg;
    height = avg;
  }

  return { width, height };
}

const baseSize = getCanvasDimensions(size, orientation); // inches
const DPI = 40;

const CANVAS_SIZE = {
  width: baseSize.width * DPI,
  height: baseSize.height * DPI
};

  // ================= SAVE CANVAS ================= //
  const handleSaveCanvas = async () => {
  const canvases = document.querySelectorAll("canvas");

  if (!canvases.length) {
    alert("No canvas found!");
    return;
  }

  const SCALE = 4; // Ultra-HD scale (2 = HD, 3 = QHD, 4 = 4K+)

  // Multiple canvases → ZIP
  if (canvases.length > 1) {
    const zip = new JSZip();
    const folder = zip.folder("photo_product_canvases");

    canvases.forEach((canvas, index) => {
      const width = canvas.width;
      const height = canvas.height;

      // Create off-screen Ultra-HD canvas
      const offscreen = document.createElement("canvas");
      offscreen.width = width * SCALE;
      offscreen.height = height * SCALE;

      const ctx = offscreen.getContext("2d");
      ctx.scale(SCALE, SCALE);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(canvas, 0, 0);

      // Convert to dataURL (lossless PNG)
      const dataURL = offscreen.toDataURL("image/png");
      const base64Data = dataURL.split(",")[1];

      folder.file(`canvas_page_${index + 1}.png`, base64Data, {
        base64: true,
      });
    });

    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, `photo_product_${productId || "canvas"}.zip`);
  } else {
    // Single canvas → Ultra-HD PNG
    const canvas = canvases[0];
    const width = canvas.width;
    const height = canvas.height;

    const offscreen = document.createElement("canvas");
    offscreen.width = width * SCALE;
    offscreen.height = height * SCALE;

    const ctx = offscreen.getContext("2d");
    ctx.scale(SCALE, SCALE);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(canvas, 0, 0);

    const dataURL = offscreen.toDataURL("image/png", 1.0);
    const link = document.createElement("a");
    link.href = dataURL;
    link.download = `photo_product_${productId || "canvas"}_ultraHD.png`;
    link.click();
  }
};

  // ================= RENDER ================= //
  return (
    <>
      {layoutExists ? (
        <div className="editor-shell">
          <TopBar onSave={handleSaveCanvas} />
          <div className="canvas-center">
            <Suspense fallback={<div>Loading canvas layout…</div>}>
              <LayoutComponent
                productId={productId}
                size={size}
                orientation={orientation}
                 canvasSize={CANVAS_SIZE}
              />
            </Suspense>
          </div>
        </div>
      ) : (
        <div className="layout-overlay">
          <div
            className="layout-missing-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="popup-title">Layout Missing</h2>
            <p className="popup-message">
              Layout for product ID <b>{productId}</b> doesn’t exist.
            </p>
            <button
              className="popup-button"
              onClick={() => window.history.back()}
            >
              Go Back
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default CanvasContainer;
